import React, { useState, useEffect } from "react";
import authAxios from "../../services/Token";
import Swal from "sweetalert2";

const VerFacturaContabilidad = ({ idFactura, show, handleClose }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingNote, setIsSendingNote] = useState(false);
  const [ncSiigo, setNcSiigo] = useState('No');

  useEffect(() => {
    if (show && idFactura) {
      cargarDatos();
    }
  }, [show, idFactura]);

  const cargarDatos = async () => {
    setIsLoading(true);
    try {
      const response = await authAxios.post("facturacion/consultar_factura", { id: idFactura });
      const resultado = response.data.dato || [];
      const ncStatus = response.data.nc_siigo || 'No';

      // Si costo_conductor es null o vacio, podemos inicializarlo con costo_tercero para la vista
      const formattedData = resultado.map((item) => ({
        ...item,
        costo_conductor_editable: item.costo_conductor !== null ? item.costo_conductor : item.costo_tercero,
      }));
      setData(formattedData);
      setNcSiigo(ncStatus);
    } catch (error) {
      console.error("Error cargando factura contabilidad:", error);
      Swal.fire("Error", "No se pudo cargar la informacion de la factura", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCostoChange = (index, value) => {
    const newData = [...data];
    newData[index].costo_conductor_editable = value;
    setData(newData);
  };

  const actualizarCosto = async (index) => {
    const item = data[index];

    try {
      const response = await authAxios.post("facturacion/actualizar_costo_conductor", {
        id: item.id,
        costo_conductor: item.costo_conductor_editable,
      });

      if (response.data.Status || response.data.status) {
        Swal.fire("Exito", response.data.msj || "Costo actualizado correctamente", "success");
        // Actualizar el estado local
        const newData = [...data];
        newData[index].costo_conductor = item.costo_conductor_editable;
        setData(newData);
      } else {
        Swal.fire("Error", response.data.msj || "No se pudo actualizar el costo", "error");
      }
    } catch (error) {
      console.error("Error actualizando costo:", error);
      Swal.fire("Error", "Ocurrio un problema al actualizar", "error");
    }
  };

  const enviarNotaContable = async () => {
    // Validar que todos los costos de conductor estén diligenciados
    const costosIncompletos = data.some(
      (item) => !item.costo_conductor_editable || item.costo_conductor_editable === 0
    );

    if (costosIncompletos) {
      Swal.fire(
        "Validación",
        "Todos los costos de conductor deben estar diligenciados y ser mayor a 0",
        "warning"
      );
      return;
    }

    // Confirmar antes de enviar
    const confirmacion = await Swal.fire({
      title: "¿Enviar Nota Contable?",
      text: "Se enviará la nota contable a Siigo. Esta acción no se puede deshacer.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, enviar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) {
      return;
    }

    setIsSendingNote(true);
    const loading = Swal.fire({
      title: "Enviando...",
      html: "Por favor espere mientras se procesa la nota contable.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await authAxios.post("facturacion/nota_contable", {
        id: idFactura,
      });

      Swal.close();

      if (response.data.Status || response.data.status) {
        setNcSiigo('Si');
        Swal.fire(
          "Éxito",
          response.data.msj || "Nota contable enviada a Siigo correctamente",
          "success"
        ).then(() => {
          cargarDatos(); // Recargar datos para actualizar el estado
        });
      } else {
        const mensajeError = response.data.msj || "No se pudo enviar la nota contable";
        const errores = response.data.errores || null;

        let textoError = mensajeError;
        if (errores) {
          textoError += "\n\nDetalles: " + JSON.stringify(errores, null, 2);
        }

        Swal.fire(
          "Error",
          textoError,
          "error"
        );
      }
    } catch (error) {
      Swal.close();
      console.error("Error enviando nota contable:", error);

      const mensajeError = error.response?.data?.msj || "Ocurrió un problema al enviar la nota contable";
      Swal.fire("Error", mensajeError, "error");
    } finally {
      setIsSendingNote(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
      <div className="modal-dialog modal-xl" style={{ maxWidth: "98%" }}>
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title">Notas Contables - Factura ID: {idFactura}</h5>
              <small className="text-muted">
                Estado: {ncSiigo === 'Si' ? (
                  <span className="badge bg-success">Nota Contable Enviada</span>
                ) : (
                  <span className="badge bg-warning">Pendiente de envío</span>
                )}
              </small>
            </div>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
          </div>
          <div className="modal-body table-responsive">
            {isLoading ? (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p>Cargando datos...</p>
              </div>
            ) : (
              <>
                <table className="table table-bordered table-sm table-striped">
                  <thead className="table-success">
                    <tr>
                      <th>Cod</th>
                      <th>Descripcion</th>
                      <th>Cant.</th>
                      <th>Valor</th>
                      <th>Total</th>
                      <th>Expediente</th>
                      <th>Placa</th>
                      <th>Conductor</th>
                      <th>Especificaciones</th>
                      <th>Costo Tercero</th>
                      <th>Costo Conductor</th>
                      <th>Accion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data && data.length > 0 ? (
                      data.map((item, id) => (
                        <tr key={id}>
                          <td>{item.codigo}</td>
                          <td style={{ fontSize: "11px" }}>{item.descripcion}</td>
                          <td>{item.cantidad}</td>
                          <td>{item.valor}</td>
                          <td>{item.total}</td>
                          <td>{item.numero_expediente}</td>
                          <td>{item.placa}</td>
                          <td style={{ fontSize: "11px" }}>{item.datos_conductor}<br/>{item.nombre_conductor}</td>
                          <td style={{ fontSize: "11px" }}>{item.especificaciones}</td>
                          <td>{item.costo_tercero}</td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm" style={{ width: "100px" }}
                              value={item.costo_conductor_editable || ""}
                              onChange={(e) => handleCostoChange(id, e.target.value)}
                              disabled={ncSiigo === 'Si'}
                            />
                          </td>
                          <td>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => actualizarCosto(id)}
                              title="Guardar Costo"
                              disabled={ncSiigo === 'Si'}
                            >
                              Guardar
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="12" className="text-center">
                          No hay datos disponibles
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Resumen de totales */}
                <div className="row mt-3 mb-3">
                  <div className="col-md-12">
                    <div className="card">
                      <div className="card-body">
                        <div className="row">
                          {/* <div className="col-md-6">
                            <h6>Total Costo Tercero:</h6>
                            <h5>
                              {data.reduce((sum, item) => sum + (parseFloat(item.costo_tercero) || 0), 0).toLocaleString('es-CO', {
                                style: 'currency',
                                currency: 'COP'
                              })}
                            </h5>
                          </div> */}
                          <div className="col-md-6">
                            <h6>Total Costo Conductor:</h6>
                            <h5>
                              {data.reduce((sum, item) => sum + (parseFloat(item.costo_conductor_editable) || 0), 0).toLocaleString('es-CO', {
                                style: 'currency',
                                currency: 'COP'
                              })}
                            </h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Cerrar
            </button>
            <button
              type="button"
              className="btn btn-success btn-lg"
              onClick={enviarNotaContable}
              disabled={isSendingNote || ncSiigo === 'Si' || data.length === 0}
              title={ncSiigo === 'Si' ? 'Nota contable ya fue enviada' : 'Enviar nota contable a Siigo'}
            >
              {isSendingNote ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Enviando...
                </>
              ) : (
                'Enviar Nota Contable'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerFacturaContabilidad;